// en-US + narrowSymbol still produces a country-prefixed dollar for these (CA$, A$, etc.).
// Override to plain $ so the symbol is consistent regardless of which locale is viewing.
const DOLLAR_PREFIXED_CURRENCIES = new Set([
    'CAD',
    'AUD',
    'NZD',
    'SGD',
    'MXN',
    'CLP'
]);
function buildCurrencyFormatOptions(currency, currencyDisplay, amount, maxFractionDigits, minFractionDigits) {
    return {
        style: 'currency',
        currency,
        currencyDisplay,
        maximumFractionDigits: maxFractionDigits,
        minimumFractionDigits: minFractionDigits ?? (Number.isInteger(amount) ? 0 : 2)
    };
}
/** ISO 4217 currency code used as fallback when no explicit currency is provided. */
export const DEFAULT_CURRENCY = 'USD';
// Numeric part types — used to find the insertion point for the K/M/B suffix.
const NUMERIC_PART_TYPES = new Set(['integer', 'decimal', 'fraction', 'group']);
function abbreviateAmount(amount) {
    const abs = Math.abs(amount);
    if (abs >= 1_000_000_000) {
        return { value: amount / 1_000_000_000, suffix: 'B' };
    }
    if (abs >= 1_000_000) {
        return { value: amount / 1_000_000, suffix: 'M' };
    }
    if (abs >= 1_000) {
        return { value: amount / 1_000, suffix: 'K' };
    }
    return { value: amount, suffix: '' };
}
function applyDollarOverride(parts, currency) {
    return parts.map(p => p.type === 'currency' && DOLLAR_PREFIXED_CURRENCIES.has(currency)
        ? '$'
        : p.value);
}
/**
 * Formats an amount as a narrow currency symbol (e.g. $1,234.56, £100, AUD → $50).
 * Dollar-prefixed currencies (CA$, A$, etc.) are overridden to plain $ for symbol consistency.
 * Pass `short: true` to abbreviate large amounts: $30K, $1.5M, $2B.
 */
export function formatCurrency(amount, options) {
    const currency = options?.currency ?? 'USD';
    const locale = options?.locale ?? 'en-US';
    const maxFractionDigits = options?.maxFractionDigits ?? 2;
    const minFractionDigits = options?.minFractionDigits;
    if (options?.short) {
        const { value: abbrevValue, suffix } = abbreviateAmount(amount);
        // Default to 1 decimal for abbreviated values (e.g. $1.5K) — no trailing zeros.
        const abbrevMax = options.maxFractionDigits ?? (suffix ? 1 : maxFractionDigits);
        const parts = new Intl.NumberFormat(locale, buildCurrencyFormatOptions(currency, 'narrowSymbol', abbrevValue, abbrevMax, 0)).formatToParts(abbrevValue);
        // Insert suffix right after the last numeric part so it works for both
        // leading-symbol ($30K) and trailing-symbol (30K €) locales.
        let lastNumericIdx = -1;
        for (let i = parts.length - 1; i >= 0; i--) {
            if (NUMERIC_PART_TYPES.has(parts[i].type)) {
                lastNumericIdx = i;
                break;
            }
        }
        return applyDollarOverride(parts, currency)
            .map((str, i) => (i === lastNumericIdx ? str + suffix : str))
            .join('');
    }
    const parts = new Intl.NumberFormat(locale, buildCurrencyFormatOptions(currency, 'narrowSymbol', amount, maxFractionDigits, minFractionDigits)).formatToParts(amount);
    return applyDollarOverride(parts, currency).join('');
}
/**
 * Returns the narrow display symbol for a currency, applying the same dollar-prefix
 * normalization as formatCurrency (CAD/AUD/NZD/SGD/MXN/CLP → $).
 * Use this when you need just the symbol, not a formatted amount.
 */
export function getCurrencySymbol(currency = 'USD', locale = 'en-US') {
    const parts = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
    }).formatToParts(0);
    const raw = parts.find(p => p.type === 'currency')?.value ?? '$';
    return DOLLAR_PREFIXED_CURRENCIES.has(currency) ? '$' : raw;
}
/**
 * Whether a currency's narrow symbol is an ambiguous `$` and therefore benefits
 * from being displayed alongside its ISO code (e.g. `$ CAD`). Mirrors
 * `DOLLAR_PREFIXED_CURRENCIES` — the single source of truth for currencies
 * that normalize to plain `$`.
 */
export function isStackedCurrency(currency = 'USD') {
    return DOLLAR_PREFIXED_CURRENCIES.has(currency);
}
/**
 * Formats an amount with its ISO 4217 currency code (e.g. USD 1,234.56, GBP 100, AUD 50).
 * Use when a formal/unambiguous representation is needed (receipts, international contexts).
 * No symbol override — codes are inherently unambiguous.
 */
export function formatCurrencyCode(amount, options) {
    const currency = options?.currency ?? 'USD';
    const locale = options?.locale ?? 'en-US';
    const maxFractionDigits = options?.maxFractionDigits ?? 2;
    const minFractionDigits = options?.minFractionDigits;
    return new Intl.NumberFormat(locale, buildCurrencyFormatOptions(currency, 'code', amount, maxFractionDigits, minFractionDigits)).format(amount);
}
/**
 * @deprecated Use formatCurrency instead.
 * Kept for @honeybook/hbui compatibility — the compiled HBCurrency component calls
 * toCurrency(amount, currency, locale, maxFractionDigits, minFractionDigits) with the
 * old positional API. Remove once hbui ships a version that calls formatCurrency.
 */
export function toCurrency(amount, currency = 'USD', locale = 'en-US', maxFractionDigits = 2, minFractionDigits) {
    return formatCurrency(amount, {
        currency,
        locale,
        maxFractionDigits,
        minFractionDigits
    });
}
//# sourceMappingURL=currency.js.map